import { useState, useContext, useRef } from "react";
import { AuthContext } from "../context/authContext";
import client from "../utils/client";
import newIdempotencyKey from "../utils/idempotencyKey";

const useWithdraw = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const { token } = useContext(AuthContext);

  // A request already in flight. Checked synchronously because two clicks can
  // land in the same React batch, before `loading` has re-rendered the button
  // into its disabled state.
  const inFlight = useRef(false);

  // Held across retries so a resubmit after a failure is recognised by the
  // server as the same withdrawal, not a second one. Cleared only on success.
  const idempotencyKey = useRef(null);

  const withdraw = async (asset, network, wallet, amount) => {
    if (inFlight.current) return;
    inFlight.current = true;

    if (!idempotencyKey.current) idempotencyKey.current = newIdempotencyKey();

    setLoading(true);
    setError(null);

    try {
      const res = await client({
        url: `/pool/withdraw`,
        data: {
          asset,
          network,
          walletAddress: wallet,
          amount,
          idempotencyKey: idempotencyKey.current,
        },
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccess(true);
      idempotencyKey.current = null;
      return res.data;
    } catch (err) {
      console.log(err);
      setError(
        err?.response?.data?.message ??
          "Could not submit the withdrawal. Please try again."
      );
      setSuccess(false);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  };

  return { withdraw, loading, error, success };
};

export default useWithdraw;
